// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Local-only implementation of useCollectionQuery backed by RxDB.
 *
 * Queries a native RxDB collection by name with an optional query builder.
 * Extracted from useCollectionQuery for mode routing.
 */

import { useEffect, useState } from 'react';

/**
 * React hook for querying an RxDB collection by name (local path only).
 *
 * Replaces `useRxData` from rxdb-hooks. Takes a database instance, collection
 * name, and optional query builder function. Subscribes to the resulting RxDB
 * query observable and returns reactive results.
 *
 * @param db - RxDatabase instance (or null/undefined)
 * @param collectionName - Name of the RxDB collection to query
 * @param queryBuilder - Optional function that receives the RxCollection and returns an RxQuery.
 *   If omitted, queries all documents via `collection.find()`.
 *   Return `null` or `undefined` to skip the query (returns empty data, not loading).
 * @returns Object with `data`, `isLoading`, and `error`
 */
export function useLocalCollectionQuery<T = Record<string, unknown>>(
  db: any,
  collectionName: string,
  queryBuilder?: (collection: any) => any
): { data: T[]; isLoading: boolean; error: Error | null } {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!db) {
      setData([]);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    const collection = db.collections?.[collectionName];
    if (!collection) {
      setData([]);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    setIsLoading(true);
    setError(null);

    try {
      const query = queryBuilder
        ? queryBuilder(collection)
        : collection.find();

      // queryBuilder returned null/undefined — skip query, return empty
      if (!query) {
        setData([]);
        setIsLoading(false);
        return undefined;
      }

      const subscription = query.$.subscribe({
        next: (docs: any[]) => {
          const mapped = docs.map((d: any) =>
            (typeof d.toJSON === 'function' ? d.toJSON() : d) as T
          );
          setData(mapped);
          setIsLoading(false);
        },
        error: (err: Error) => {
          setError(err);
          setIsLoading(false);
        },
      });

      return () => subscription.unsubscribe();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsLoading(false);
      return undefined;
    }
  }, [db, collectionName, queryBuilder]);

  return { data, isLoading, error };
}
